/**
 * Performance logging utility for tracking application load times
 * 
 * Usage:
 * 1. Call initializePerfLog() at app startup
 * 2. Enable with ?isPerfLogEnabled=true in URL
 * 3. Import and use setPerfLog with constants:
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * 
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 * 
 * // View results:
 * console.log(window.perfLog);
 * 
 * Design:
 * - setPerfLog() stores data in a flat Map (O(1) operation)
 * - Duplicate calls with the same key+type are silently discarded
 * - window.perfLog getter does ALL the work:
 *   - Builds the hierarchical tree from the flat map
 *   - Applies parent-child relationships based on PERF_PATH_MAP
 *   - Sorts nodes by time
 *   - Returns the complete structured tree
 * - This keeps the hot path (setPerfLog) extremely fast
 */

import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { PERF_EVENT,PERF_KEYS, PERF_PATH_MAP } from './constants';
import { captureResourceTimings, findOrCreateNode, sortPerfNodes } from './helpers';
import { PerfEventType, PerfKeyType, PerfLogOptions, PerfNode } from './types';

// Re-export types and constants for convenience
export type { PerfNode, ResourceTiming, ResourceMatcher, PerfEventType, PerfKeyType, PerfLogOptions } from './types';
export { PERF_KEYS, PERF_EVENT } from './constants';

// ==================== Module State ====================

// Flat storage: key -> { START?, COMPLETE? }
// All tree building is deferred to the getter
// Keys can be either a PerfKeyType or a composite key like "LOAD_CONTEXT_BOOK_API.viz-123"
interface PerfLogEntry {
    START?: number;
    COMPLETE?: number;
}

const perfLogMap = new Map<string, PerfLogEntry>();
const URL_PARAM_IS_PERF_LOG_ENABLED = 'isPerfLogEnabled';
// Cached perf log enabled state (null = not yet checked, avoids parsing query params on every call)
let isPerfLogEnabled: boolean | null = null;

/**
 * Checks if perf logging is enabled (caches result after first check)
 */
const checkPerfLogEnabled = (): boolean => {
    if (isPerfLogEnabled === null) {
        try {
            const queryParams = getQueryParamsAndQueryString().queryParams;
            isPerfLogEnabled = queryParams[URL_PARAM_IS_PERF_LOG_ENABLED] === 'true';
        } catch (e) {
            // If anything fails, disable perf logging
            isPerfLogEnabled = false;
        }
    }
    return isPerfLogEnabled;
};

/**
 * Logs a performance event - JUST stores in flat map (O(1))
 * @param key - The event key (must be one of PERF_KEYS constants)
 * @param type - Event type: PERF_EVENT.START or PERF_EVENT.COMPLETE
 * @param options - Optional settings: { trackingId?, val? }
 *                  - trackingId: Tracking ID for multi-instance tracking (creates nested children)
 *                  - val: Custom timestamp (defaults to performance.now())
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * 
 * // Simple usage
 * setPerfLog(PERF_KEYS.APP_INIT_START, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 * 
 * // With custom timestamp
 * setPerfLog(PERF_KEYS.DOCUMENT_PARSE, PERF_EVENT.COMPLETE, { val: perfMeta.docParseEnd });
 * 
 * // For multiple widgets, pass a trackingId to track each separately:
 * setPerfLog(PERF_KEYS.LOAD_CONTEXT_BOOK_API, PERF_EVENT.START, { trackingId: 'viz-123' });
 * setPerfLog(PERF_KEYS.LOAD_CONTEXT_BOOK_API, PERF_EVENT.COMPLETE, { trackingId: 'viz-123' });
 */
export const setPerfLog = (key: PerfKeyType, type: PerfEventType, options?: PerfLogOptions) => {
    try {
        if (!checkPerfLogEnabled()) {
            return;
        }
        // Ignore if key is not in PERF_PATH_MAP
        if (!(key in PERF_PATH_MAP)) {
            return;
        }

        const { trackingId, val } = options ?? {};

        // Early exit if trackingId key is explicitly provided but value is null/undefined
        if (options && 'trackingId' in options && !trackingId) {
            return;
        }

        // Create composite key when trackingId is provided (e.g., "LOAD_CONTEXT_BOOK_API.viz-123")
        const storageKey = trackingId ? `${key}.${trackingId}` : key;

        let entry = perfLogMap.get(storageKey);
        if (!entry) {
            entry = {};
            perfLogMap.set(storageKey, entry);
        }

        // Only set value if not already set (discard duplicate updates for same key+trackingId+type)
        if (entry[type] === undefined) {
            entry[type] = val ?? performance.now();
            
            // Also set performance marker
            try {
                const markerName = `${storageKey}--${type}`;
                performance.mark(markerName);
            } catch (e) {
                // Silently fail if performance API is not available
            }
        }
        // If value already exists, silently discard this update
    } catch (e) {
        // Silently fail if performance API is not available
    }
};

/**
 * Builds the hierarchical tree from the flat perfLogMap
 * This does ALL the work: tree building, parent-child relationships, sorting
 * Called only when window.perfLog is accessed
 * 
 * Handles both regular keys (e.g., "APP_INIT_START") and composite keys with
 * trackingIds (e.g., "LOAD_CONTEXT_BOOK_API.viz-123") to create nested child nodes.
 */
const buildTreeFromMap = (): PerfNode[] => {
    const tree: PerfNode[] = [];
    
    // Iterate through all logged entries in the flat map
    Array.from(perfLogMap.entries()).forEach(([storageKey, entry]) => {
        // Check if this is a composite key with a trackingId (e.g., "LOAD_CONTEXT_BOOK_API.viz-123")
        const dotIndex = storageKey.indexOf('.');
        const baseKey = dotIndex !== -1 ? storageKey.substring(0, dotIndex) : storageKey;
        const trackingId = dotIndex !== -1 ? storageKey.substring(dotIndex + 1) : null;
        
        // Resolve the parent path for the base key from PERF_PATH_MAP
        let parentPath: string = PERF_PATH_MAP[baseKey as PerfKeyType];
        
        // Handle $trackingId placeholder in parent path
        // This allows trackingId to be inserted INTO the parent path instead of appended at the end
        // e.g., "PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId" + trackingId "vizId" 
        //       -> "PINBOARD_VIZ_CONTAINER_VIEWPORT.vizId.VIZ_RENDER_QUEUE_PRIMARY"
        const hasTrackingIdPlaceholder = parentPath?.includes('$trackingId');
        if (hasTrackingIdPlaceholder && trackingId) {
            parentPath = parentPath.replace('$trackingId', trackingId);
        }
        
        // Build the full path: parentPath + baseKey + trackingId (if no placeholder)
        let fullPathParts: string[];
        if (parentPath) {
            fullPathParts = parentPath.split(".").concat(baseKey);
        } else {
            fullPathParts = [baseKey];
        }
        
        // If there's a trackingId and no placeholder was used, add it as a child node under the base key
        if (trackingId && !hasTrackingIdPlaceholder) {
            fullPathParts.push(trackingId);
        }
        
        // Find or create the node in the tree (builds parent-child structure)
        const node = findOrCreateNode(tree, fullPathParts);
        
        // Copy the START and COMPLETE values to the node, converting to seconds
        if (entry.START !== undefined) {
            node.START = parseFloat((entry.START / 1000).toFixed(3));
        }
        if (entry.COMPLETE !== undefined) {
            node.COMPLETE = parseFloat((entry.COMPLETE / 1000).toFixed(3));
        }
    });
    
    return tree;
};

// ==================== Public API ====================

/**
 * Initializes the performance logging system
 * Only active when isPerfLogEnabled=true is in the URL
 * Uses Object.defineProperty getter to build and return the tree on-demand
 */
export const initializePerfLog = () => {
    try {
        if (checkPerfLogEnabled()) {
            // Expose setPerfLog globally for cross-package access (e.g., viz-render-queue in ts-packages)
            (window as any).setPerfLog = setPerfLog;
            
            // Define perfLog as a property with a getter that builds the tree on-demand
            Object.defineProperty(window, 'perfLog', {
                get() {
                    try {
                        // Build the tree from the flat map (all tree logic happens here)
                        const tree = buildTreeFromMap();
                        
                        // Capture resource timings and add to tree
                        const resourceTimings = captureResourceTimings();
                        Object.entries(resourceTimings).forEach(([key, timing]) => {
                            tree.push({
                                key,
                                START: timing.start,
                                COMPLETE: timing.complete,
                                steps: [],
                            });
                        });
                        
                        // Sort the tree by START time (fallback to COMPLETE time)
                        // All transformation happens here in the getter
                        return sortPerfNodes(tree);
                    } catch (e) {
                        // Silently fail and return empty array
                        return [];
                    }
                },
                configurable: true,
            });
            
            // Handle docParseEnd from perfMeta if available
            if ((window as any).perfMeta?.docParseEnd) {
                setPerfLog('DOCUMENT_PARSE', 'COMPLETE', { val: (window as any).perfMeta.docParseEnd });
            }
        }
    } catch (e) {
        // Silently fail if initialization fails
    }
};
